import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ArrowDown, ShieldCheck, Clock } from "lucide-react";
import { MaskedLine } from "@/components/MaskedLine";

const HERO_IMG = "/images/hero-signature.png";

export const KineticHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden rb-grain">
      <motion.div className="absolute inset-0 scale-110" style={{ y: bgY }}>
        <img
          src={HERO_IMG}
          alt="Jardin entretenu par RB Espace Vert"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#121F17]/70 via-[#121F17]/35 to-[#121F17]/90" />

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-40 w-full"
      >
        <MaskedLine delay={0.15}>
          <span
            data-testid="hero-status-badge"
            className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-mono-rb uppercase tracking-[0.25em] text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-4 py-2 mb-8 bg-[#121F17]/40 backdrop-blur-sm"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
            Élagueur-grimpeur à Montreuil — Devis sous 24h
          </span>
        </MaskedLine>

        <h1 className="font-serif-rb text-[#F5F3EE] text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl" data-testid="hero-headline">
          <MaskedLine delay={0.3}>L'art de tailler,</MaskedLine>
          <MaskedLine delay={0.45}>
            <span className="italic text-[#8CA893]">soigner</span> &amp; sublimer
          </MaskedLine>
          <MaskedLine delay={0.6}>vos espaces verts.</MaskedLine>
        </h1>

        <MaskedLine delay={0.85}>
          <p className="mt-8 max-w-xl text-base sm:text-lg text-[#F5F3EE]/75 leading-relaxed" data-testid="hero-subtitle">
            Élagage, jardinage paysager et arboriculture — RB Espace Vert maîtrise tout ce qui
            touche à l'entretien et à l'aménagement de vos jardins, avec la rigueur d'un artisan
            et le respect du vivant.
          </p>
        </MaskedLine>

        <MaskedLine delay={1.05}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-devis"
              className="group inline-flex items-center gap-3 bg-[#F5F3EE] text-[#121F17] font-semibold rounded-full px-8 py-4 transition-colors duration-300 hover:bg-[#D4AF37]"
            >
              Demander un devis gratuit
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
            <a
              href="#avis"
              data-testid="hero-cta-avis"
              className="inline-flex items-center gap-2 text-[#F5F3EE]/85 border border-[#F5F3EE]/25 rounded-full px-6 py-4 text-sm hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors duration-300"
            >
              <Star className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
              4,5/5 — Voir les 17 avis Google
            </a>
          </div>
        </MaskedLine>

        <MaskedLine delay={1.25}>
          <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-[#F5F3EE]/60 text-sm" data-testid="hero-trust-row">
            <span className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#8CA893]" /> Travail soigné &amp; sécurisé
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#8CA893]" /> Rapide &amp; efficace
            </span>
            <span className="flex items-center gap-2">
              <Star className="w-4 h-4 text-[#8CA893]" /> Explications détaillées à chaque intervention
            </span>
          </div>
        </MaskedLine>
      </motion.div>

      <motion.div
        className="absolute bottom-8 right-8 hidden lg:block text-[#F5F3EE]/40"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <ArrowDown className="w-5 h-5" />
      </motion.div>
    </section>
  );
};
